import { useNavigate } from 'react-router-dom';
import { useToast, Toast, ToastType } from '../../hooks/useToast';

const TOAST_CONFIG: Record<ToastType, { icon: string; color: string; bg: string; border: string; dot: string }> = {
  // ── Shipment statuses ──────────────────────────────────────────────────
  transit: {
    icon: 'ri-truck-line',
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/20',
    dot: 'bg-orange-400',
  },
  delivered: {
    icon: 'ri-checkbox-circle-line',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/20',
    dot: 'bg-green-400',
  },
  hold: {
    icon: 'ri-pause-circle-line',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/20',
    dot: 'bg-yellow-400',
  },
  out_for_delivery: {
    icon: 'ri-e-bike-2-line',
    color: 'text-teal-400',
    bg: 'bg-teal-400/10',
    border: 'border-teal-400/20',
    dot: 'bg-teal-400',
  },
  picked_up: {
    icon: 'ri-box-3-line',
    color: 'text-sky-400',
    bg: 'bg-sky-400/10',
    border: 'border-sky-400/20',
    dot: 'bg-sky-400',
  },
  // ── General UI toasts ─────────────────────────────────────────────────
  success: {
    icon: 'ri-checkbox-circle-fill',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/25',
    dot: 'bg-emerald-400',
  },
  info: {
    icon: 'ri-information-line',
    color: 'text-teal-400',
    bg: 'bg-teal-400/10',
    border: 'border-teal-400/20',
    dot: 'bg-teal-400',
  },
  warning: {
    icon: 'ri-error-warning-line',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/20',
    dot: 'bg-amber-400',
  },
  error: {
    icon: 'ri-close-circle-fill',
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/25',
    dot: 'bg-red-400',
  },
};

// Fallback for any unknown type
const FALLBACK_CONFIG = {
  icon: 'ri-notification-3-line',
  color: 'text-teal-400',
  bg: 'bg-teal-400/10',
  border: 'border-teal-400/20',
  dot: 'bg-teal-400',
};

function ToastItem({ toast }: { toast: Toast }) {
  const { removeToast } = useToast();
  const navigate = useNavigate();
  const cfg = TOAST_CONFIG[toast.type] ?? FALLBACK_CONFIG;

  return (
    <div
      className={`flex items-start gap-3 w-80 bg-[#1e2235]/95 backdrop-blur-xl border ${cfg.border} rounded-xl p-4 animate-slide_up`}
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
    >
      {/* Icon */}
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
        <i className={`${cfg.icon} text-lg`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
          <p className="text-white font-semibold text-xs truncate">{toast.title}</p>
        </div>
        <p className="text-gray-400 text-xs leading-relaxed">{toast.message}</p>
        {toast.trackingId && (
          <button
            onClick={() => navigate(`/track/${toast.trackingId}`)}
            className={`mt-2 text-xs font-medium cursor-pointer transition-colors ${cfg.color} hover:underline whitespace-nowrap`}
          >
            View shipment <i className="ri-arrow-right-line" />
          </button>
        )}
      </div>

      {/* Close */}
      <button
        onClick={() => removeToast(toast.id)}
        className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-gray-300 cursor-pointer transition-colors flex-shrink-0 mt-0.5"
      >
        <i className="ri-close-line text-sm" />
      </button>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts } = useToast();
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
